import React, { useEffect, useContext, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateToggle } from "@features/toggledSlice";
import { updateActiveTab } from "@features/activeTabSlice";
import { gridH, getActiveModule, titleCase } from "@ts/utils";
import {
    IToast,
    ITitle,
    IToggle,
    LayoutProps,
    IPortfolio,
    IGlobalGridApi,
    IValueBoolean,
    IMainContext,
    Portfolio,
    TActiveClientId,
    ParentModule,
    IClientAccess,
    IAccess,
    Platforms,
    TUserdata,
    SubModule,
    ICampaignCreationDisplay,
} from "@ts/common-types";
import { setPortfoliosData } from "@features/gridDataSlice";
import { mainContext } from "@ts/constants";
import { populatePortfolios } from "@ts/retrieve-data";
import SideBar from "./SideBar";
import Footer from "./Footer";
import Header from "./Header";
import CustomModal from "@modals/CustomModal";
import CustomToast from "@modals/CustomToast";
import TrendsModal from "@modals/TrendsModal";
import ExpiryTimeCalculator from "@modals/session-warning-modal/ExpiryTimeCalculator";
import BulkTaxonomyModal from "@modals/bulk-taxonomy-modal/BulkTaxonomyModal";
import DayPartingModal from "@modals/day-parting-modal/DayPartingModal";
import AdminSettingsModal from "@modals/admin-settings/AdminSettingsModal";
import RequestModal from "@components/modals/RequestModal";
import CreateKeywordsReviewModal from "@modals/bulk-action-modal/CreateKeywordsReviewModal";
import BulkActionModal from "@modals/bulk-action-modal/BulkActionModal";
import SubHeader from "./SubHeader";
import {
    updateAddToExactModalDisp,
    updateBulkActionModal,
} from "@features/modalSlice";
import { useGetModulesData, useActivationChannelInit } from "@hooks/AllCustomHooks";
import DailyBudgetReviewModal from "@components/modals/DailyBudgetReviewModal";
import AddToExactModal from "@components/modals/add-to-exact-modal/AddToExactModal";
import CampaignConfigChangeModal from "@components/modals/CampaignConfigChangeModal";
import CpmUpdateModal from "@components/modals/CpmUpdateModal";
import AddGoalsModal from "@components/modals/AddGoalsModal";
import BulkTagAdditionModal from "@components/modals/BulkTagAdditionModal";
import BulkProductMappingModal from "@components/modals/BulkProductMappingModal";
import AddMediaPlanModal from "@/components/modals/AddMediaPlanModal";
import Apply1DsMappingModal from "@components/modals/Apply1DSMappingModal";
import Apply1DsKeywordMappingModal from "@components/modals/Apply1DSKeywordMappingModal";
import ConfirmModal from "@/components/modals/ConfirmModal";
import RevstackLayoutHeader from "@/components/widgets/filter-widgets/RevstackLayoutHeader";
import * as Sentry from "@sentry/react";

type IModalDisplay = {
    bulkActionModalDisp?: IValueBoolean;
    bulkTaxonomyDisplay?: IValueBoolean;
    showAdminSettingsDisplay?: IValueBoolean;
    showCreateKeywordsReviewDisplay?: IValueBoolean;
    dailyBudgetReviewModalDisplay?: IValueBoolean;
    addToExactModalDisp?: IValueBoolean;
    campaignConfigModalDisplay?: IValueBoolean;
    updateCpmModalDisplay?: IValueBoolean;
    addGoalsModalDisplay?: IValueBoolean;
    addBulkTagsModalDisplay?: IValueBoolean;
    bulkProductMappingModalDisplay?: IValueBoolean;
    addMediaPlanModalDisplay?: IValueBoolean;
    apply1DsMappingModalDisplay?: IValueBoolean;
    apply1DsKeywordMappingModalDisplay?: IValueBoolean;
};

const Layout = React.memo(
    ({
        children,
        isHeaderRequired = true,
        isFooterRequired = true,
    }: LayoutProps) => {
        const globalGridApi = useSelector(
            (state: IGlobalGridApi) => state.globalGridApi.value
        );
        const title = useSelector((state: ITitle) => state.title.value);
        const toggle = useSelector((state: IToggle) => state.toggle.value);
        const toastData = useSelector((state: IToast) => state.toastData);
        const portfoliosData = useSelector(
            (state: IPortfolio) => state.portfoliosData.value
        );
        const { show } = toastData;
        const dispatch = useDispatch();
        const layoutStyles = `page-wrapper chiller-theme ${toggle}`;
        const bulkActionModalDisp = useSelector(
            (state: IModalDisplay) => state.bulkActionModalDisp.value
        );

        const addToExactModalDisp = useSelector(
            (state: IModalDisplay) => state.addToExactModalDisp.value
        );
        const { activeModule } = getActiveModule();
        const { dates, DateRange } = useContext<IMainContext>(mainContext);

        const showBulkTaxonomy = useSelector(
            (state: IModalDisplay) => state.bulkTaxonomyDisplay.value
        );

        const showAdminSettings = useSelector(
            (state: IModalDisplay) => state.showAdminSettingsDisplay.value
        );

        const showCreateKeywordsReviewDisplay = useSelector(
            (state: IModalDisplay) =>
                state.showCreateKeywordsReviewDisplay.value
        );

        const showDailyBudgetReviewModal = useSelector(
            (state: IModalDisplay) => state.dailyBudgetReviewModalDisplay.value
        );

        const showCampaignConfigModal = useSelector(
            (state: IModalDisplay) => state.campaignConfigModalDisplay.value
        );

        const showUpdateCpmModal = useSelector(
            (state: IModalDisplay) => state.updateCpmModalDisplay.value
        );

        const showAddGoalsModal = useSelector(
            (state: IModalDisplay) => state.addGoalsModalDisplay.value
        );

        const showBulkTagsAdditionModal = useSelector(
            (state: IModalDisplay) => state.addBulkTagsModalDisplay.value
        );

        const showBulkProductMappingModal = useSelector(
            (state: IModalDisplay) => state.bulkProductMappingModalDisplay.value
        );

        const showApply1DsMappingModal = useSelector(
            (state: IModalDisplay) => state.apply1DsMappingModalDisplay.value
        );

        const showApply1DsKeywordMappingModal = useSelector(
            (state: IModalDisplay) => state.apply1DsKeywordMappingModalDisplay.value
        );

        const activeClientId: number = useSelector(
            (state: TActiveClientId) => +state.activeClientId.value
        );

        const customModalDisp = useSelector(
            (state: any) => state.customModalDisp.value
        );

        const showAddMediaPlanModal = useSelector(
            (state: IModalDisplay) => state.addMediaPlanModalDisplay.value
        );

        const showConfirmationModal = useSelector((state: any) => state.confirmModalDisplay);
        const campaignCreationDisplay = useSelector(
            (state: ICampaignCreationDisplay) => state.campaignCreationDisplay.value
        );

        const isSubheaderVisible = useGetModulesData({
            type: "config",
        })?.isSubheaderVisible && !campaignCreationDisplay;

        const userData = useSelector(
            (state: TUserdata) => state.userData.value
        );

        const userAccess = userData?.access?.find((access: IAccess) =>
            access.clients.find(
                (client: IClientAccess) => client.client_id === activeClientId
            )
        );

        const platformAccess = useMemo(() => {
            return userAccess
                ? userAccess?.platforms.map((item: string) =>
                      item.toLowerCase()
                  )
                : [];
        }, [userAccess]);

        const isMeasurementOnlyAccess =
            platformAccess.length === 1 &&
            platformAccess.includes(Platforms.MEASUREMENT);

        useActivationChannelInit();

        const showSidebarWindow = () => {
            window.innerWidth < 921
                ? dispatch(updateToggle(""))
                : dispatch(updateToggle("toggled"));
        };

        useEffect(() => {
            gridH();
            window.addEventListener("resize", () => {
                showSidebarWindow();
                gridH();
            });
        });

        useEffect(() => {
            document.title = `${title} | REVINITI`;
            if (title === titleCase(ParentModule.PERFORMANCE_OVERVIEW)) {
                dispatch(updateActiveTab(SubModule.OVERVIEW));
            }
        }, [title, dispatch]);

        useEffect(() => {
            if (
                (activeModule === ParentModule.KEYWORD_ANALYTICS ||
                    activeModule === ParentModule.PRODUCT_ANALYTICS) &&
                !portfoliosData.length
            ) {
                populatePortfolios()
                    .then((portfolios) => {
                        dispatch(
                            setPortfoliosData(portfolios as Array<Portfolio>)
                        );
                    })
                    .catch((error) => {
                        console.error(error);
                        Sentry.captureException(error);
                    });
            }
            // eslint-disable-next-line
        }, [activeClientId, dates, DateRange, dispatch]);

        return (
            <>
                <div className={layoutStyles}>
                    <SideBar />
                    <main
                        className="page-content"
                        id="main-wrapper"
                        style={{
                            backgroundColor: campaignCreationDisplay
                                ? "#F0F4F8"
                                : "#fff",
                        }}
                    >
                        {isHeaderRequired &&
                            !isMeasurementOnlyAccess &&
                            //TODO: refactor this...
                            ([
                                titleCase(SubModule.CATEGORY_ANALYSIS),
                                titleCase(SubModule.PRODUCT_ANALYSIS),
                                titleCase(SubModule.NAVIGATOR),
                            ].includes(title) ? (
                                <RevstackLayoutHeader />
                            ) : (
                                <Header />
                            ))}
                        {isSubheaderVisible && <SubHeader />}
                        {children}
                    </main>
                    {isFooterRequired && <Footer />}
                    {customModalDisp && <CustomModal />}
                    <TrendsModal />
                    <RequestModal />
                    {showBulkTaxonomy && (
                        <BulkTaxonomyModal
                            showBulkTaxonomy={showBulkTaxonomy}
                        />
                    )}
                    {showAdminSettings && (
                        <AdminSettingsModal {...{ showAdminSettings }} />
                    )}
                    {showCreateKeywordsReviewDisplay && (
                        <CreateKeywordsReviewModal
                            {...{ showCreateKeywordsReviewDisplay }}
                        />
                    )}
                    <DayPartingModal />
                    {show === true ? (
                        <CustomToast toastData={toastData} />
                    ) : null}
                </div>
                <ExpiryTimeCalculator />
                {bulkActionModalDisp && (
                    <BulkActionModal
                        show={bulkActionModalDisp}
                        setShow={(showModal) =>
                            dispatch(updateBulkActionModal(showModal))
                        }
                        gridApi={globalGridApi}
                    />
                )}
                {addToExactModalDisp && (
                    <AddToExactModal
                        show={addToExactModalDisp}
                        setShow={(showModal: boolean) =>
                            dispatch(updateAddToExactModalDisp(showModal))
                        }
                        gridApi={globalGridApi}
                    />
                )}
                {showDailyBudgetReviewModal && (
                    <DailyBudgetReviewModal
                        showDailyBudgetReviewModal={showDailyBudgetReviewModal}
                    />
                )}
                {showCampaignConfigModal && (
                    <CampaignConfigChangeModal show={showCampaignConfigModal} />
                )}
                {showUpdateCpmModal && (
                    <CpmUpdateModal show={showUpdateCpmModal} />
                )}
                {showAddGoalsModal && (
                    <AddGoalsModal show={showAddGoalsModal} />
                )}
                {showBulkTagsAdditionModal && (
                    <BulkTagAdditionModal show={showBulkTagsAdditionModal} />
                )}
                {showBulkProductMappingModal && (
                    <BulkProductMappingModal
                        show={showBulkProductMappingModal}
                    />
                )}

                {showAddMediaPlanModal && (
                    <AddMediaPlanModal show={showAddMediaPlanModal} />
                )}

                {showApply1DsMappingModal && (
                    <Apply1DsMappingModal show={showApply1DsMappingModal} />
                )}

                {showApply1DsKeywordMappingModal && (
                    <Apply1DsKeywordMappingModal
                        show={showApply1DsKeywordMappingModal}
                    />
                )}

                {showConfirmationModal.value && (
                    <ConfirmModal modalData={showConfirmationModal} />
                )}
            </>
        );
    }
);

export default Layout;
